/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the display of the
 * 3 Advertisment set up in administration panel
 */

// Imports
import { faMagnifyingGlass, faVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { JSX, useEffect, useState } from "react";
import { PublicCabinet } from "../../interfaces/ICabinets";

export interface Tutorial {
    title?: string;
    tags?: string[];
    source: string;
    thumbnail?: string;
}

export const BCTutorials = (props): JSX.Element => {
    const cabinet = JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet;
    const [rowMatchingTutos, setRowMatchingTutos] = useState<Tutorial[][]>([]);
    const [searchKeywords, setSearchKeywords] = useState<string>("");

    // Define all tutorials
    const tutos: Tutorial[] = [
        { title: "Module de formation MS Copilot", source: "/tutorials/MSCopilot.mp4", thumbnail: "/tutorials/thumbnail/msCopilot.png", tags: ["copilot", "ia", "intelligence", "artificielle", "microsoft", "365", "copilot"] },
        { title: "Module de formation MS Outlook", source: "/tutorials/MSOutlook.mp4", thumbnail: "/tutorials/thumbnail/msOutlook.png", tags: ["sécurisé", "mails", "conversation", "microsoft", "365", "outlook"] },
        { title: "Module de formation MS Teams", source: "/tutorials/MSTeams.mp4", thumbnail: "/tutorials/thumbnail/msTeams.png", tags: ["conférence", "camera", "partage", "communication", "microsoft", "365", "teams"] },
        { title: "Module de formation MS OneDrive", source: "/tutorials/MSOneDrive.mp4", thumbnail: "/tutorials/thumbnail/msOneDrive.png", tags: ["sécurisé", "sauvegarde", "espace", "partagé", "microsoft", "365", "onedrive"] },
        { title: "Module de formation MS Word", source: "/tutorials/MSWord.mp4", thumbnail: "/tutorials/thumbnail/msWord.png", tags: ["rédaction", "documents", "ecriture", "microsoft", "365", "word"] },
        { title: "Espace Client - cabinet", source: "/tutorials/ECLCabinet.mp4", thumbnail: "/tutorials/thumbnail/ecl.jpg", tags: ["espace", "client", "communication", "externe", "partage", "sécurité"] },
        { title: "Module Buro.Secure", source: "/tutorials/BuroSecure.mp4", thumbnail: "/tutorials/thumbnail/secure.jpg", tags: ["espace", "client", "sécurité", "securite", "mail"] },
        { title: "Espace Client - client", source: "/tutorials/ECLClient.mp4", thumbnail: "/tutorials/thumbnail/eclclient.jpg", tags: ["espace", "client", "communication", "externe", "partage", "sécurité"] },
        { title: "Module Buro.AddON", source: "/tutorials/BuroAddON.mp4", thumbnail: "/tutorials/thumbnail/addon.jpg", tags: ["addon", "add.on", "add-on", "microsoft", "365", "outlook", "word", "excel", "interopérabilité"] },
        { title: "Module Buro.Calendar", source: "/tutorials/BuroCalendar.mp4", thumbnail: "/tutorials/thumbnail/calendar.jpg", tags: ["calendrier", "documents", "temps", "burocalendar", "calendar", "mobile", "smartphone", "contact", "agenda"] },
        { title: "Module Buro.RDVWeb", source: "/tutorials/BuroRDVWeb.mp4", thumbnail: "/tutorials/thumbnail/rdvweb.jpg", tags: ["consultation", "visio", "consultation visio", "site", "internet", "site internet", "en ligne", "rdv", "rendez-vous", "rdvweb"] },
        { title: "Module Buro.PDF", source: "/tutorials/BuroPDF.mp4", thumbnail: "/tutorials/thumbnail/pdf.jpg", tags: ["pdf", "pièces", "pieces", "modifier"] },
        { title: "Module TN Tampon Numérique", source: "/tutorials/BuroTNTampon.mp4", thumbnail: "/tutorials/thumbnail/tn.jpg", tags: ["tampon", "numérique", "tn", "pièces", "signature", "signer", "pdf", "tamponner", "tamponnage"] },
        { title: "Module TN Bordereau", source: "/tutorials/BuroTNBordereau.mp4", thumbnail: "/tutorials/thumbnail/tnbordereau.jpg", tags: ["bordereaux", "pièces", "pieces", "pdf"] },
        { title: "Module Buro.RPVA", source: "/tutorials/BuroRPVA.mp4", thumbnail: "/tutorials/thumbnail/rpva.jpg", tags: ["rpva", "audience", "appel", "juridiction"] },

    ];

    const filterTutorialOnKeywords = (wholeKeywords: string): Tutorial[] => {
        if (!wholeKeywords) return tutos;

        const keywords: string[] = wholeKeywords.toLowerCase().split(/\s+/); // get each keyword in lowercase
        return tutos.filter((tuto) => {
            // Split into words + lowercase
            const titleWords: string[] = tuto.title?.toLowerCase().split(/\s+/) || [];
            const tagWords: string[] = tuto.tags?.map(tag => tag.toLowerCase()) || [];

            // Check if any keyword is matching either something in the title or in tags
            return keywords.some((keyword) => {
                return titleWords.some(word => word.includes(keyword)) || tagWords.some(word => word.includes(keyword));
            });
        })
    }

    /* eslint-disable */
    useEffect(() => {
        // Get tutorials matching the search keywords
        const foundTutos: Tutorial[] = filterTutorialOnKeywords(searchKeywords);
        console.log(foundTutos);

        // Chunk them by chunk of 3 tutorials
        const rowFoundTutos: Tutorial[][] = foundTutos.reduce<Tutorial[][]>((acc, _, i) => (i % 3 === 0 ? [...acc, foundTutos.slice(i, i + 3)] : acc), []);

        setRowMatchingTutos(rowFoundTutos);
        console.log(rowFoundTutos);
    }, [searchKeywords]);
    /* eslint-enable */

    return (
        <div className={`flex flex-col my-3 mx-4 overflow-auto ${props.className ? props.className : ''}`}>

            {/* Buroclic Tutorials */}
            <div className="flex flex-col flex-grow mt-2">

                {/* Title */}
                <div className="flex flex-row h-8 mb-4 place-items-center">
                    <div className="font-bold my-2 grow">
                        <span className='text-md lg:text-lg'><FontAwesomeIcon className={`w-8 h-8`} icon={faVideo} style={{ color: cabinet.color + 'CF' }} />    Mes tutoriels</span>
                        <hr className={`mt-1 border-1 opacity-100 rounded-lg w-[30%]`} style={{ borderColor: cabinet.color }} />
                    </div>
                </div>

                {/* Searchbar */}
                <div className='w-[95.5%]'><input tabIndex={7} type="text" placeholder="Rechercher" value={searchKeywords} onChange={(e) => setSearchKeywords(e.target.value)}
                    className="py-3 pl-10 pr-3 border border-gray-300 w-1/2 h-12 rounded-lg text-gray-500" />
                    <FontAwesomeIcon className="relative w-6 h-6 right-1/2 mx-2" icon={faMagnifyingGlass} />
                </div>

                {/* Tutorials */}
                {rowMatchingTutos.length > 0 ? rowMatchingTutos.map((row, rowIndex) => (
                    <div key={'searchKeywords' + rowIndex} className={`lg:px-[2.5%] px-[10%] flex flex-wrap ${row.length === 2 ? 'justify-around' : 'justify-between'} mr-0 min-h-[200px] w-[100%] lg:my-4 mt-4 mb-0 `}>
                        {row.map((tuto, index) => (
                            <video className={`lg:w-[30%] w-full lg:mb-0 ${index === 2 ? 'mb-0' : 'sm:mb-4'} h-auto`} controls tabIndex={20 + index} controlsList="nodownload"
                                key={'tuto-' + index} poster={`${tuto.thumbnail ? tuto.thumbnail : 'buroclic.png'}`}>
                                <source src={tuto.source} />
                                <p>Votre navigateur ne supporte malheureusement pas l'affichage de vidéo HTML5.</p>
                            </video>
                        ))}
                    </div>
                )) : <div className="text-center mt-8 italic">Aucun tutoriel correspondant à votre recherche n'a été trouvé.</div>}
            </div>
        </div>
    );
}

BCTutorials.propTypes = {
    className: PropTypes.string
}